import reduceAnalysisToolUI, {AnalysisToolUIState} from 'analysis-tool/reduceAnalysisUI';
import reduceCookieConsentModal, {CookieConsentModalState} from 'analytics/reduceCookieConsentModal';
import reduceChemistryUI, {ChemistryUIState} from 'chemistry/reduceChemistryUI';
import reduceInsightsUI, {InsightsUIState} from 'insights/reduceInsightsUI';
import reduceAutomationFileStatus, {
  AutomationFileStatusState,
} from 'lab-automation/automation-files/reduceAutomationFileStatus';
import reduceMetadataTab, {MetadataTabsState} from 'metadata/reduceMetadataTab';
import reduceModbioUI, {ModbioUIState} from 'modbio/redux/reduceModbioUI';
import reduceAllBulkAssemblies from 'molbio/bulk-assembly/reduceAllBulkAssemblies';
import reduceAllCodonOptimizationTabs, {
  CodonOptimizationTabsState,
} from 'molbio/codon-optimization/reduceAllCodonOptimizationTabs';
import reduceMolbioUI, {MolbioUIState} from 'molbio/reduceMolbioUI';
import reduceDataTablesUI, {DataTablesState} from 'notebook/data-tables/reduceDataTablesUI';
import reduceDocumentExportUI, {DocumentsExportState} from 'notebook/entries/reduceDocumentExportUI';
import reduceDocumentEditorLockState, {
  DocumentEditorLockState,
} from 'notebook/realtime/reduceDocumentEditorLockState';
import reduceReviewCriteriaConfigUI, {
  ReviewCriteriaConfigState,
} from 'notebook/reviews/reduceReviewCriteriaConfigUI';
import reduceFormTableSwitcherState, {
  FormTableSwitcherState,
} from 'notebook/structured-tables/action-functions/form-table-switcher/reduceFormTableState';
import reduceStructuredTableEditState, {
  StructuredTableTogglableEditState,
} from 'notebook/structured-tables/action-functions/toggle-edit-mode/reduceToggledTableEditState';
import {reduceCollapsedTablesUI, CollapsedTableState} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import reducePresence, {PresenceState} from 'realtime/reducePresence';
import reduceRealtimeEncryptionState, {RealtimeEncryptionState} from 'realtime/reduceRealtimeEncryptionState';
import reduceRequestsUI, {RequestsUIState} from 'requests/reduceRequestsUI';
import reduceResultsUI, {ResultsUIState} from 'results/reduceResultsUI';
import reduceTagsUI, {TagsUIState} from 'runs/assay-group/reduceTagsUI';
import reduceSamplesUI, {SamplesUIState} from 'samples/reduceSamplesUI';
import reduceSchemaConfig, {SchemaConfigUIState} from 'schemas/spec/reduceSchemaConfigUI';
import reduceSearchUI, {SearchUIState} from 'search/reduceSearchUI';
import reduceTenantAdminUI, {TenantAdminUIState} from 'tenants/admin/reduceTenantAdminUI';
import reduceGlobalClientStates, {
  GlobalClientStates,
} from 'ui-library/hooks/global-client-state/reduceGlobalClientStates';
import reduceMenusUI, {MenusUIState} from 'ui-library/menus/reduceMenusUI';
import reduceScrollableTableUI, {ScrollableTableUIState} from 'ui-library/scrollable/reduceScrollableTableUI';
import combineBenchlingReducers from 'util/combineBenchlingReducers';
import reduceWarehouseUI, {WarehouseUIState} from 'warehouse/reduceWarehouseUI';
import reduceWorkflowDetailsTable, {
  WorkflowDetailsTableState,
} from 'workflows/components/reduceWorkflowDetailsTable';
import reduceWorkflowTemplateSelection, {
  WorkflowTemplateSelectionState,
} from 'workflows/template-selection/reduceWorkflowTemplateSelection';
import reduceWorkflowVisualization, {
  WorkflowVisualizationState,
} from 'workflows/visualization/reduceWorkflowVisualization';
import reduceWorklistSidepanelState, {WorklistSidepanelState} from 'worklists/reduceWorklistSidepanelActions';

export interface UIState {
  analysisTool: AnalysisToolUIState;
  automationFileStatus: AutomationFileStatusState;
  chemistry: ChemistryUIState;
  codonOptimizationTab: CodonOptimizationTabsState;
  collapsedTables: CollapsedTableState;
  cookieConsentModal: CookieConsentModalState;
  dataTables: DataTablesState;
  documentEditorLockState: DocumentEditorLockState;
  export: DocumentsExportState;
  formTableSwitcherState: FormTableSwitcherState;
  globalClientStates: GlobalClientStates;
  insights: InsightsUIState;
  menus: MenusUIState;
  metadataTab: MetadataTabsState;
  modbio: ModbioUIState;
  molbio: MolbioUIState;
  presence: PresenceState;
  realtimeEncryption: RealtimeEncryptionState;
  requests: RequestsUIState;
  results: ResultsUIState;
  reviewCriteriaConfig: ReviewCriteriaConfigState;
  samples: SamplesUIState;
  schemaConfig: SchemaConfigUIState;
  schemaTags: TagsUIState;
  scrollableTables: ScrollableTableUIState;
  structuredTableEditState: StructuredTableTogglableEditState;
  search: SearchUIState;
  tenantAdmin: TenantAdminUIState;
  warehouse: WarehouseUIState;
  workflowDetailsTable: WorkflowDetailsTableState;
  workflowTemplateSelection: WorkflowTemplateSelectionState;
  workflowVisualization: WorkflowVisualizationState;
  worklistSidepanelState: WorklistSidepanelState;
}

export default combineBenchlingReducers({
  analysisTool: reduceAnalysisToolUI,
  automationFileStatus: reduceAutomationFileStatus,
  bulkAssemblies: reduceAllBulkAssemblies,
  chemistry: reduceChemistryUI,
  codonOptimizationTab: reduceAllCodonOptimizationTabs,
  collapsedTables: reduceCollapsedTablesUI,
  cookieConsentModal: reduceCookieConsentModal,
  dataTables: reduceDataTablesUI,
  documentEditorLockState: reduceDocumentEditorLockState,
  export: reduceDocumentExportUI,
  formTableSwitcherState: reduceFormTableSwitcherState,
  globalClientStates: reduceGlobalClientStates,
  insights: reduceInsightsUI,
  menus: reduceMenusUI,
  metadataTab: reduceMetadataTab,
  modbio: reduceModbioUI,
  molbio: reduceMolbioUI,
  presence: reducePresence,
  realtimeEncryption: reduceRealtimeEncryptionState,
  requests: reduceRequestsUI,
  results: reduceResultsUI,
  reviewCriteriaConfig: reduceReviewCriteriaConfigUI,
  samples: reduceSamplesUI,
  schemaConfig: reduceSchemaConfig,
  schemaTags: reduceTagsUI,
  scrollableTables: reduceScrollableTableUI,
  structuredTableEditState: reduceStructuredTableEditState,
  search: reduceSearchUI,
  tenantAdmin: reduceTenantAdminUI,
  warehouse: reduceWarehouseUI,
  workflowDetailsTable: reduceWorkflowDetailsTable,
  workflowTemplateSelection: reduceWorkflowTemplateSelection,
  workflowVisualization: reduceWorkflowVisualization,
  worklistSidepanelState: reduceWorklistSidepanelState,
});
